import { Apps, AutoFixHigh, Image, Layers, Movie, PictureAsPdf } from '@mui/icons-material';
import { Box, Card, CardMedia, styled, useTheme, type CardMediaProps } from '@mui/material';
import { forwardRef, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { Tooltip } from '~/components/Tooltip';
import { Pptx } from '~/components/icons';
import { Link, type LinkProps } from '~/components/link';
import { TagList } from '~/components/tag';
import { formatInterval } from '~/lib/datetime';
import {
  VIDEO_LIKE_KINDS,
  mediaDimensions,
  mediaExtension,
  mediaSize,
  videoDuration,
} from '~/lib/media';
import { formatBytes } from '~/lib/units';
import { CardActionsTitleText } from '../cards';
import { OverlaySettingsDialog } from '../overlays/settings-dialog';
import type {
  ContentCard__ContentItem,
  ContentCard__MediaItem,
  ContentThumbnail__MediaItem,
} from './card.generated';

type OptionalTags = { tags?: ReadonlyArray<string> };
type PartialContentItem = Omit<ContentCard__ContentItem, 'tags'> & OptionalTags;
type PartialMediaItem = Omit<ContentCard__MediaItem, 'tags'> & OptionalTags;
export type PartialContent = PartialContentItem | PartialMediaItem;

export const ContentIcon = ({ item }: { item: PartialContent }) => {
  const Icon =
    item.__typename === 'ContentItem'
      ? item.design?.id
        ? AutoFixHigh
        : Apps
      : item.kind === 'IMAGE'
      ? Image
      : item.kind === 'PDF'
      ? PictureAsPdf
      : item.kind === 'POWERPOINT'
      ? Pptx
      : Movie;

  return <Icon sx={{ color: 'primary.dark' }} />;
};

interface ContentMetaProps {
  item: PartialContent;
  /**
   * Whether the overlay marker is a button that opens the overlay settings. Off when the whole
   * card is a button (a MediaSelect card), since a button cannot hold another.
   */
  interactive?: boolean;
}

const ContentMeta = ({ interactive = true, item }: ContentMetaProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [overlayDialogOpen, setOverlayDialogOpen] = useState(false);
  return (
    <>
      <ContentIcon item={item} />
      {item.__typename === 'ContentItem' && (item.design?.id ? 'DESIGN' : 'APP')}
      {item.__typename === 'MediaItem' && mediaExtension(item)}
      {item.overlay && (
        <>
          <div>•</div>
          {interactive ? (
            <IconButton
              label={t('common.tooltips.update_overlay_settings')}
              style={{ padding: 0, color: theme.palette.primary.dark }}
              onClick={() => {
                setOverlayDialogOpen(true);
              }}
            >
              <Layers />
            </IconButton>
          ) : (
            <Layers sx={{ color: 'primary.dark' }} />
          )}
          <Tooltip title={t('common.tooltips.overlay_id', { id: item.overlay.id })}>
            <span>OVERLAY</span>
          </Tooltip>
        </>
      )}
      {interactive && (
        <OverlaySettingsDialog
          close={() => setOverlayDialogOpen(false)}
          item={item}
          open={overlayDialogOpen}
        />
      )}
    </>
  );
};

export const ContentCardMeta = ({ interactive, item }: ContentMetaProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        color: 'text.secondary',
        fontSize: 13,
      }}
    >
      <ContentMeta interactive={interactive} item={item} />
    </Box>
  );
};

// Media Card Actions Component
interface ContentCardActionsProps extends ContentMetaProps {
  children?: ReactNode;
  small?: boolean;
}

export const ContentCardActions = ({
  children,
  interactive,
  item,
  small = false,
}: ContentCardActionsProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 1.5,
        pb: 1,
        pr: 0,
        borderTop: 1,
        borderTopColor: 'borders.light',
      }}
    >
      <CardActionsTitleText title={item.name} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            fontSize: 13,
            ...(small && {
              '& svg': {
                fontSize: 13,
              },
              '& > div': {
                fontSize: 10,
              },
            }),
          }}
        >
          <ContentCardMeta interactive={interactive} item={item} />
        </Box>
        {children}
      </Box>
    </Box>
  );
};

export const ContentThumbnailContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  position: 'relative',
  '&::before': {
    content: '""',
    display: 'block',
    paddingBottom: 'calc(100% / 16 * 9)',
  },
  '& > :first-of-type': {
    backgroundColor: theme.palette.common.white,
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
  },
}));

export interface CardThumbnailProps extends CardMediaProps<'img'> {
  readonly item: PartialContentItem | ContentThumbnail__MediaItem;
}

export const ContentCardThumbnail = forwardRef<HTMLImageElement, CardThumbnailProps>(
  function CardThumbnail({ children, className, item, ...props }, ref) {
    return (
      <ContentThumbnailContainer className={className}>
        {item.__typename === 'ContentItem' && !item.thumbnailUri ? (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
                backgroundColor: 'primary.dark',
              }}
            >
              <Apps sx={{ height: '100%', width: '35%', color: 'common.white' }} />
            </Box>
          </Box>
        ) : (
          <CardMedia
            alt={item.name}
            image={item.thumbnailUri ?? undefined}
            crossOrigin="anonymous"
            {...props}
            component="img"
            ref={ref}
          />
        )}
        {children}
      </ContentThumbnailContainer>
    );
  },
);

const Overlay = styled(Box, { shouldForwardProp: (prop) => prop !== 'visible' })<{
  visible: boolean;
}>(({ theme, visible }) => ({
  display: 'flex',
  position: 'absolute',
  top: 0,
  left: 0,
  padding: theme.spacing(0, 1, 0.5),
  color: theme.palette.common.white,
  fontSize: 14,
  fontWeight: 600,
  width: '100%',
  height: '100%',
  alignItems: 'flex-end',
  backgroundColor: 'rgba(0, 0, 0, .3) !important',
  opacity: visible ? 1 : 0,
  transition: 'opacity 0.3s',
  '&:hover': {
    opacity: 1,
  },
}));

export const GridCard = styled(Card)(({ theme }) => ({
  height: '100%',
  '&:hover': {
    boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 8px 0px',
  },
  // The card clips its content. A link fills the card, so ring the card itself; other controls
  // get an inset ring.
  '&:has(a:focus-visible)': theme.mixins.focusRing,
  '& a:focus-visible': { outline: 'none', boxShadow: 'none' },
  // Doubled to outrank the controls' own focus styles, which are inserted after this.
  '&& :focus-visible, && .Mui-focusVisible': { outlineOffset: -2 },
}));
export interface ContentCardProps {
  LinkProps?: LinkProps;
  checked?: boolean | null;
  children: ReactNode;
  item: PartialContent;
}

export const ContentCard = ({ LinkProps, checked, children, item }: ContentCardProps) => {
  const { dimensions, duration, extension, size } = useMemo(() => {
    if (item.__typename === 'ContentItem') return {};
    return {
      dimensions: mediaDimensions(item).join('x'),
      duration: VIDEO_LIKE_KINDS.includes(item.kind)
        ? formatInterval(videoDuration(item), true)
        : '',
      extension: mediaExtension(item),
      size: formatBytes(mediaSize(item)),
    };
  }, [item]);

  const thumbnail = (
    <ContentCardThumbnail item={item}>
      <Overlay
        visible={checked ?? false}
        className="overlay-section"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ paddingY: 0.5, maxWidth: '100%', overflow: 'hidden' }}>
          <TagList abbreviate item={item} sx={{ flexDirection: 'column' }} tags={item.tags || []} />
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          {duration}
          <br />
          {dimensions}
          <br />
          {extension}
          <br />
          {size}
        </Box>
      </Overlay>
    </ContentCardThumbnail>
  );

  return (
    <GridCard>
      {LinkProps != null ? (
        <Link
          {...LinkProps}
          // An app without a thumbnail shows only an icon, and the overlay's tags and dimensions
          // would otherwise make up the name of a card that has one.
          aria-label={item.name}
          sx={{ display: 'block', ...LinkProps.sx }}
        >
          {thumbnail}
        </Link>
      ) : (
        thumbnail
      )}
      <ContentCardActions item={item}>{children}</ContentCardActions>
    </GridCard>
  );
};
